import {
  Autocomplete,
  FormControl,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
} from '@mui/material';
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import InputHeader from '../../../components/InputHeader';
import { useAppDataContext } from '../../../contexts/appData';
import { useCurrentUser } from '../../../contexts/currentUser';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import useContractSelectOptions, {
  ContractSelectItem,
} from '../../../hooks/useContractSelectOptions';
import useQueryErrorNotifier from '../../../hooks/useQueryErrorNotifier';
import {
  JourneyFormSubmittedValues,
  JourneyFormValues,
  JourneyInfo,
} from '../../../types/journey';
import { PortSelectItem } from '../../../types/port';
import {
  buildDayjsTimeCompatibleString,
  DAYJS_DATA_DATE_FORMAT,
  DAYJS_DATA_TIME_FORMAT,
  DAYJS_VISUALIZATION_DATE_FORMAT,
} from '../../../utils/date';
import {
  prepareAllowedPorts,
  renderPortSelectItemLabel,
} from '../../../utils/port';
import { CommonMainPanelForm } from '../../../components/AdminTemplates';
import useIotsAdminForm from '../../../hooks/useIotsAdminForm';
import { JourneyStatus, ServiceType } from '../../../types/core';

export interface JourneyFormProps {
  onSubmit: (values: JourneyFormSubmittedValues, callback: () => void) => void;
  onCancel?: () => void;
  onDayOrTimeChange: (day: string | null, time: string | null) => void;
  isLoading: boolean;
  journey?: JourneyInfo | null;
  disabled?: boolean;
}

const JourneyForm = ({
  onSubmit,
  onCancel,
  onDayOrTimeChange,
  isLoading,
  disabled = false,
  journey = null,
}: JourneyFormProps) => {
  const { t } = useLanguage();
  const { userOperator } = useCurrentUser();
  const { ports, portsMap } = useAppDataContext();
  const { getContracts, getContractById } = useIotsApi();

  const isMdUp = useMediaQuery('(min-width:768px)');

  const {
    data: fetchedContracts = [],
    isFetching: isFetchingContracts,
    error: contractsError,
  } = useQuery({
    queryKey: ['contracts'],
    queryFn: () => getContracts(),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    select: contracts =>
      [...contracts].sort(
        (a, b) =>
          (a.operatorName ?? '').localeCompare(b.operatorName ?? '') ||
          a.name.localeCompare(b.name),
      ),
  });

  const { contracts, contractsMap } = useContractSelectOptions(
    fetchedContracts,
    userOperator,
  );

  const [selectedContractId, setSelectedContractId] = useState<string | null>(
    null,
  );

  const {
    data: selectedContract = null,
    isFetching: isFetchingSelectedContract,
    error: selectedContractError,
  } = useQuery({
    queryKey: ['contracts', selectedContractId ?? ''],
    queryFn: () =>
      selectedContractId
        ? getContractById(selectedContractId)
        : Promise.resolve(null),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const [minDateFrom, maxDateTo] = useMemo<Array<Dayjs | null>>(() => {
    if (!selectedContract) {
      return [null, null];
    }
    return [dayjs(selectedContract.dateFrom), dayjs(selectedContract.dateTo)];
  }, [selectedContract]);

  const { allowedPorts, allowedPortsMap } = useMemo(() => {
    if (!selectedContract) {
      return { allowedPorts: ports, allowedPortsMap: portsMap };
    }

    return prepareAllowedPorts(ports, selectedContract);
  }, [selectedContract, ports, portsMap]);

  useQueryErrorNotifier([contractsError, selectedContractError]);

  const initialValues = useMemo<JourneyFormValues>(() => {
    if (!journey) {
      return {
        contractId: null,
        serviceType: null,
        departurePortId: null,
        arrivalPortId: null,
        day: null,
        time: null,
      };
    }

    return {
      contractId: journey.contractId,
      serviceType: journey.serviceType,
      departurePortId: journey.departurePortId,
      arrivalPortId: journey.arrivalPortId,
      day: dayjs(journey.day),
      time: dayjs(buildDayjsTimeCompatibleString(journey.time)),
    };
  }, [journey]);

  const areInputsDisabled =
    disabled ||
    isLoading ||
    isFetchingSelectedContract ||
    journey?.journeyStatus === JourneyStatus.Cancelled ||
    journey?.journeyStatus === JourneyStatus.ReliefEvent;

  const {
    formState,
    materialInputHelpers: {
      getTextFieldProps,
      getSelectProps,
      getBaseProps,
      renderError,
    },
  } = useIotsAdminForm(
    {
      isEditingByDefault: !journey,
      initialValues,
      onEditionCancel: onCancel,
      onSubmit: (values, callback) => {
        onSubmit(
          {
            operatorId: contractsMap[values.contractId as string].operatorId,
            contractId: values.contractId,
            serviceType: values.serviceType as ServiceType,
            departurePortId: values.departurePortId as string,
            arrivalPortId: values.arrivalPortId as string,
            day: values.day as Dayjs,
            time: values.time as Dayjs,
          },
          callback,
        );
      },
      validate: (values, setError) => {
        if (!values.contractId) {
          setError('contractId', t('validation.required-field'));
        } else if (
          // It checks that no invalid contract Id has been selected
          !contractsMap[values.contractId] ||
          (userOperator &&
            userOperator.operatorId !==
              contractsMap[values.contractId].operatorId)
        ) {
          setError('contractId', t('validation.invalid-option'));
        }

        if (selectedContract && selectedContract.id === values.contractId) {
          if (!values.serviceType) {
            setError('serviceType', t('validation.required-field'));
          }
          if (!values.departurePortId) {
            setError('departurePortId', t('validation.required-field'));
          } else if (
            !allowedPortsMap[values.departurePortId] ||
            allowedPortsMap[values.departurePortId].issue
          ) {
            setError('departurePortId', t('validation.invalid-option'));
          }

          if (!values.arrivalPortId) {
            setError('arrivalPortId', t('validation.required-field'));
          } else if (values.arrivalPortId === values.departurePortId) {
            setError(
              'arrivalPortId',
              t('validation.value-equal-to-another-field', {
                ANOTHER_FIELD: t('content.departure-port'),
              }),
            );
          } else if (
            !allowedPortsMap[values.arrivalPortId] ||
            allowedPortsMap[values.arrivalPortId].issue
          ) {
            setError('arrivalPortId', t('validation.invalid-option'));
          }

          if (!values.day) {
            setError('day', t('validation.required-field'));
          } else if (!values.day.isValid()) {
            setError('day', t('validation.invalid-date'));
          } else if (minDateFrom && values.day.isBefore(minDateFrom, 'day')) {
            setError(
              'day',
              t('validation.date-before-reference-date', {
                REFERENCE_DATE: minDateFrom.format(
                  DAYJS_VISUALIZATION_DATE_FORMAT,
                ),
              }),
            );
          } else if (maxDateTo && values.day.isAfter(maxDateTo, 'day')) {
            setError(
              'day',
              t('validation.date-after-reference-date', {
                REFERENCE_DATE: maxDateTo.format(
                  DAYJS_VISUALIZATION_DATE_FORMAT,
                ),
              }),
            );
          }
        }

        if (!values.time) {
          setError('time', t('validation.required-field'));
        } else if (!values.time.isValid()) {
          setError('time', t('validation.invalid-time'));
        }
      },
      resetOnInitialValuesChange: () => Boolean(journey),
    },
    {
      shouldInputsBeDisabled: areInputsDisabled,
    },
  );

  const {
    handleSubmit,
    hasVisibleErrors,
    haveValuesChanged,
    values,
    updateValues,
    touchField,
    isEditing,
    requestEdition,
    cancelEdition,
  } = formState;

  useEffect(() => {
    // The state value is in sync with the value in the form so it can be used for querying
    setSelectedContractId(values.contractId);
  }, [values.contractId]);

  useEffect(() => {
    // Only updates form values if the contract matches the one selected and there are missing values
    if (
      selectedContract &&
      selectedContract.id === values.contractId &&
      (!values.serviceType || !values.departurePortId || !values.arrivalPortId)
    ) {
      updateValues({
        serviceType: values.serviceType ?? selectedContract.serviceType,
        departurePortId:
          values.departurePortId ?? selectedContract.defaultPortOfDepartureId,
        arrivalPortId:
          values.arrivalPortId ?? selectedContract.defaultPortOfArrivalId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContract, updateValues]);

  useEffect(() => {
    onDayOrTimeChange(
      values.day?.isValid() ? values.day.format(DAYJS_DATA_DATE_FORMAT) : null,
      values.time?.isValid()
        ? values.time.format(DAYJS_DATA_TIME_FORMAT)
        : null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.day, values.time]);

  return (
    <CommonMainPanelForm
      isEditing={isEditing}
      onSubmit={handleSubmit}
      isSubmitDisabled={
        areInputsDisabled || !haveValuesChanged || hasVisibleErrors
      }
      onCancelEdition={cancelEdition}
      onEditionRequest={requestEdition}
      isEditDisabled={areInputsDisabled}
    >
      <div className="grid grid-rows-[min-content 1fr] h-full">
        <div className="mb-4">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <label className="block mb-4">
              <InputHeader required>{t('content.contract')}</InputHeader>
              <FormControl error={getBaseProps('contractId').error} fullWidth>
                <Autocomplete<ContractSelectItem>
                  fullWidth
                  size={isMdUp ? 'medium' : 'small'}
                  value={contractsMap[values.contractId ?? ''] ?? null}
                  onChange={(_event, selectedOption) =>
                    updateValues(
                      {
                        contractId: selectedOption?.value ?? '',
                        serviceType: null,
                        departurePortId: null,
                        arrivalPortId: null,
                      },
                      true,
                    )
                  }
                  options={contracts}
                  onBlur={() => touchField('contractId')}
                  loading={isFetchingContracts}
                  disabled={Boolean(
                    getBaseProps('contractId').disabled ||
                      isFetchingContracts ||
                      journey,
                  )}
                  renderInput={params => (
                    <TextField
                      {...params}
                      fullWidth
                      size={isMdUp ? 'medium' : 'small'}
                      error={getTextFieldProps('contractId').error}
                      placeholder={t('content.select')}
                    />
                  )}
                  getOptionLabel={contract => contract.label}
                />
                {renderError('contractId')}
              </FormControl>
            </label>
            <label className="block mb-4">
              <InputHeader required>{t('content.service-type')}</InputHeader>
              <FormControl error={getBaseProps('serviceType').error} fullWidth>
                <Select<string>
                  {...getSelectProps('serviceType')}
                  fullWidth
                  displayEmpty
                  disabled /* This selector doesn't make sense anymore as non-subisized journeys are not supported */
                  renderValue={selected => {
                    if (!selected) {
                      return (
                        <span className="text-iots-grey-400">
                          {t('content.select')}
                        </span>
                      );
                    }
                    return t(`service-types.${selected}.name`);
                  }}
                >
                  {Object.values(ServiceType)
                    .filter(
                      serviceType =>
                        ![
                          ServiceType.AirService,
                          ServiceType.FacilityManagement,
                        ].includes(serviceType),
                    )
                    .map(serviceType => (
                      <MenuItem key={serviceType} value={serviceType}>
                        {t(`service-types.${serviceType}.name`)}
                      </MenuItem>
                    ))}
                </Select>
                {renderError('serviceType')}
              </FormControl>
            </label>
            <label className="block mb-4 w-full">
              <InputHeader required>{t('content.departure-port')}</InputHeader>
              <FormControl
                error={getBaseProps('departurePortId').error}
                fullWidth
              >
                <Autocomplete<PortSelectItem>
                  fullWidth
                  value={allowedPortsMap[values.departurePortId ?? ''] ?? null}
                  onChange={(_event, selectedOption) => {
                    updateValues(
                      { departurePortId: selectedOption?.value || null },
                      true,
                    );
                  }}
                  options={allowedPorts}
                  onBlur={() => touchField('departurePortId')}
                  disabled={
                    getBaseProps('departurePortId').disabled ||
                    !values.contractId
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      fullWidth
                      error={getBaseProps('departurePortId').error}
                      placeholder="Select..."
                    />
                  )}
                  size="small"
                  getOptionLabel={port => renderPortSelectItemLabel(port, t)}
                  getOptionDisabled={port => Boolean(port.issue)}
                />
                {renderError('departurePortId')}
              </FormControl>
            </label>
            <label className="block mb-4 w-full">
              <InputHeader required>{t('content.arrival-port')}</InputHeader>
              <FormControl
                error={getBaseProps('arrivalPortId').error}
                fullWidth
              >
                <Autocomplete<PortSelectItem>
                  fullWidth
                  value={allowedPortsMap[values.arrivalPortId ?? ''] ?? null}
                  onChange={(_event, selectedOption) => {
                    updateValues(
                      { arrivalPortId: selectedOption?.value || null },
                      true,
                    );
                  }}
                  options={allowedPorts}
                  onBlur={() => touchField('arrivalPortId')}
                  disabled={
                    getBaseProps('arrivalPortId').disabled || !values.contractId
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      fullWidth
                      error={getBaseProps('arrivalPortId').error}
                      placeholder="Select..."
                    />
                  )}
                  size="small"
                  getOptionLabel={port => renderPortSelectItemLabel(port, t)}
                  getOptionDisabled={port => Boolean(port.issue)}
                />
                {renderError('arrivalPortId')}
              </FormControl>
            </label>
            <label className="block mb-4 w-full">
              <InputHeader required>{t('content.date')}</InputHeader>
              <FormControl error={getBaseProps('day').error} fullWidth>
                <DatePicker
                  className="w-full"
                  disabled={Boolean(
                    getBaseProps('day').disabled ||
                      journey ||
                      !values.contractId,
                  )}
                  format={DAYJS_VISUALIZATION_DATE_FORMAT}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: isMdUp ? 'medium' : 'small',
                      className: 'py-1.5',
                      error: getBaseProps('day').error,
                      onBlur: () => touchField('day'),
                    },
                  }}
                  value={values.day}
                  onChange={newValue =>
                    updateValues(
                      {
                        day: newValue,
                      },
                      true,
                    )
                  }
                  minDate={minDateFrom ?? undefined}
                  maxDate={maxDateTo ?? undefined}
                />
                {renderError('day')}
              </FormControl>
            </label>
            <label className="block w-full">
              <InputHeader required>{t('content.time')}</InputHeader>
              <FormControl error={getBaseProps('time').error} fullWidth>
                <TimePicker
                  ampm={false}
                  className="w-full"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: isMdUp ? 'medium' : 'small',
                      onBlur: () => touchField('time'),
                      error: getBaseProps('time').error,
                    },
                  }}
                  value={values.time}
                  onChange={newValue =>
                    updateValues(
                      {
                        time: newValue,
                      },
                      true,
                    )
                  }
                  disabled={getBaseProps('time').disabled}
                />
                {renderError('time')}
              </FormControl>
            </label>
          </LocalizationProvider>
        </div>
      </div>
    </CommonMainPanelForm>
  );
};

export default JourneyForm;
